// CoderQueue — cola del coder (lista master del workspace).
// Header con segmentos (chips), selector de location y búsqueda; tabla densa
// seleccionable con columnas Paciente · Dx · HCC · Estado · RAF$ · Últ. review.
import React, { useContext, useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  TextField,
  InputAdornment,
  MenuItem,
  Skeleton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InboxIcon from "@mui/icons-material/Inbox";
import RateReviewIcon from "@mui/icons-material/RateReview";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import AllInboxIcon from "@mui/icons-material/AllInbox";
import PlaceIcon from "@mui/icons-material/Place";

import { T, MONO } from "../common/tokens";
import { SEGMENTS, formatUsd } from "../common/dispositions";
import { LocationContext } from "../../../context/MasterContext/LocationContext";
import { formatDate } from "../../../utils/formatters/date";
import CodeChip from "./CodeChip";
import StatusPill from "./StatusPill";

const SEGMENT_ICONS = { InboxIcon, RateReviewIcon, TaskAltIcon, AllInboxIcon };

// Fecha relativa compacta para "Últ. review" (hoy / -3d / —).
function relativeDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const today = new Date();
  const diffDays = Math.round((today - d) / 86400000);
  if (diffDays <= 0) return "today";
  if (diffDays === 1) return "-1d";
  if (diffDays < 30) return `-${diffDays}d`;
  return formatDate(iso, "—");
}

function SegmentChip({ seg, active, onClick }) {
  const Icon = SEGMENT_ICONS[seg.icon] || InboxIcon;
  return (
    <Box
      onClick={onClick}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        fontSize: "11px",
        cursor: "pointer",
        userSelect: "none",
        border: `1px solid ${active ? T.primary : T.lineStrong}`,
        bgcolor: active ? T.primary50 : T.surface,
        color: active ? T.primary700 : T.ink2,
        fontWeight: active ? 600 : 400,
        borderRadius: "5px",
        px: 1,
        py: "3px",
        transition: "all 120ms",
      }}
    >
      <Icon sx={{ fontSize: 13 }} />
      {seg.label}
    </Box>
  );
}

const HEAD_SX = {
  position: "sticky",
  top: 0,
  bgcolor: T.surface2,
  textAlign: "left",
  fontWeight: 600,
  fontSize: "11px",
  letterSpacing: ".3px",
  textTransform: "uppercase",
  color: T.ink3,
  p: "8px 10px",
  borderBottom: `1px solid ${T.line}`,
  zIndex: 1,
};

const CELL_SX = { p: "7px 10px", borderBottom: `1px solid ${T.line}`, whiteSpace: "nowrap" };

export default function CoderQueue({
  rows,
  loading,
  total,
  segment,
  onSegment,
  query,
  onQuery,
  locationId,
  onLocation,
  selectedId,
  onSelect,
}) {
  const { locations } = useContext(LocationContext);

  const locationOptions = useMemo(() => {
    const list = Array.isArray(locations) ? locations : [];
    return list
      .map((l) => ({
        id: l.locationId ?? l.id ?? l.idLocation,
        name: l.medicalCenter || l.centerName || l.name || l.center || `Location ${l.locationId}`,
      }))
      .filter((l) => l.id != null);
  }, [locations]);

  return (
    <Box
      sx={{
        borderRight: `1px solid ${T.line}`,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        bgcolor: T.surface,
      }}
    >
      {/* Header de la cola */}
      <Box sx={{ p: "9px 12px", borderBottom: `1px solid ${T.line}`, bgcolor: T.surface2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: "13px", fontWeight: 600, color: T.ink }}>
            Coder queue
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: "11px", color: T.ink3, fontFamily: MONO }}>
            {loading ? "…" : `${rows.length} / ${total} items`}
          </Typography>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          {SEGMENTS.map((seg) => (
            <SegmentChip
              key={seg.key}
              seg={seg}
              active={segment === seg.key}
              onClick={() => onSegment(seg.key)}
            />
          ))}
        </Stack>

        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <TextField
            size="small"
            placeholder="Search patient · CIP · code…"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            sx={{ flex: 1, "& .MuiInputBase-root": { fontSize: 13, bgcolor: T.surface } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: T.ink3 }} />
                </InputAdornment>
              ),
            }}
          />
          <TextField
            size="small"
            select
            value={locationId}
            onChange={(e) => onLocation(e.target.value)}
            sx={{ minWidth: 150, "& .MuiInputBase-root": { fontSize: 13, bgcolor: T.surface } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PlaceIcon sx={{ fontSize: 16, color: T.ink3 }} />
                </InputAdornment>
              ),
            }}
          >
            <MenuItem value="">All locations</MenuItem>
            {locationOptions.map((l) => (
              <MenuItem key={l.id} value={l.id}>
                {l.name}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
      </Box>

      {/* Tabla */}
      <Box sx={{ overflow: "auto", minHeight: 0, flex: 1 }}>
        <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
          <Box component="thead">
            <Box component="tr">
              <Box component="th" sx={HEAD_SX}>Patient</Box>
              <Box component="th" sx={HEAD_SX}>Dx</Box>
              <Box component="th" sx={HEAD_SX}>HCC</Box>
              <Box component="th" sx={HEAD_SX}>Status</Box>
              <Box component="th" sx={{ ...HEAD_SX, textAlign: "right" }}>
                <Tooltip title="Estimated RAF$ — standard estimate">
                  <span>RAF $ · est.</span>
                </Tooltip>
              </Box>
              <Box component="th" sx={HEAD_SX}>Last DOS</Box>
            </Box>
          </Box>
          <Box component="tbody">
            {loading
              ? Array.from({ length: 10 }).map((_, i) => (
                  <Box component="tr" key={`sk-${i}`}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <Box component="td" key={j} sx={CELL_SX}>
                        <Skeleton variant="text" width={j === 0 ? 140 : 60} />
                      </Box>
                    ))}
                  </Box>
                ))
              : rows.map((op, idx) => {
                  const sel = op.id === selectedId;
                  return (
                    <Box
                      component="tr"
                      key={op.id ?? idx}
                      onClick={() => onSelect(op.id)}
                      sx={{
                        cursor: "pointer",
                        borderLeft: `3px solid ${sel ? T.primary : "transparent"}`,
                        bgcolor: sel ? T.primary50 : idx % 2 ? T.surface2 : T.surface,
                        "&:hover": { bgcolor: T.primary50 },
                      }}
                    >
                      <Box component="td" sx={{ ...CELL_SX, fontWeight: sel ? 600 : 400, color: T.ink }}>
                        {op.memberFullName || "—"}
                      </Box>
                      <Box component="td" sx={CELL_SX}>
                        <CodeChip title={op.diagnosisDescription}>{op.diagnosisCode}</CodeChip>
                      </Box>
                      <Box component="td" sx={CELL_SX}>
                        <CodeChip title={op.hccDescription}>{op.hccId}</CodeChip>
                      </Box>
                      <Box component="td" sx={CELL_SX}>
                        <StatusPill status={op.status} />
                      </Box>
                      <Box
                        component="td"
                        sx={{ ...CELL_SX, textAlign: "right", fontFamily: MONO, color: T.ink }}
                      >
                        {op.rafDollars ? `+${formatUsd(op.rafDollars)}` : "—"}
                      </Box>
                      <Box component="td" sx={{ ...CELL_SX, fontFamily: MONO, color: T.ink3 }}>
                        {relativeDate(op.lastDos)}
                      </Box>
                    </Box>
                  );
                })}
          </Box>
        </Box>

        {!loading && rows.length === 0 && (
          <Stack alignItems="center" justifyContent="center" spacing={1} sx={{ py: 6, px: 3, textAlign: "center" }}>
            <InboxIcon sx={{ fontSize: 38, color: T.ink3 }} />
            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.ink2 }}>
              No pending opportunities in this view
            </Typography>
            <Typography sx={{ fontSize: 12, color: T.ink3 }}>
              Try another segment or location.
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
